import indicatorLocalContextSvg from '@disy/cadenza-icons/analysis-context-data-view.svg';
import indicatorAnalysisContextSvg from '@disy/cadenza-icons/analysis-context.svg';
import scaleDependentIcon from '@disy/cadenza-icons/map.svg';
import { icon } from '@disy/ui/icon';
import type { MenuItemGroup } from '@disy/ui/menu';

import { getStore } from 'cadenza/store/cadenza-store';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import { isMap } from 'cadenza/workbook/api/data-view-visualization-key';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { modifyDataViewAttribute } from 'cadenza/workbook/data-view/data-view-utils';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { isAnalysisContextFilterActive } from 'cadenza/workbook/view/view-filters-button/view-filters-button-settings';
import { assertActiveAttributeId } from 'cadenza/workbook/workbook-attribute-util';
import { getAttributeById, getDataViewById } from 'cadenza/workbook/workbook-core-selectors.state';
import i18n from 'cadenza/workbook/workbook-panel/settings/attributes/data-view-bundle-controller.properties';

export interface HierarchyInfo {
  id: AttributeId | ActiveAttributeId;
  usesLocalHierarchyLevel?: boolean;
  usesScaleDependentHierarchyLevel?: boolean;
  supportsScaleDependentHierarchyControl?: boolean;
}

type DisabledFn = () => boolean;
interface MenuActionOptions {
  disabledFn?: DisabledFn;
  checked?: boolean;
}

export function isHierarchyControlledByMapScale(
  dataViewId: DataViewId,
  hierarchy: WorkbookAttributeDto | HierarchyInfo,
) {
  return Boolean(
    isMap(getDataViewById(getStore().getState(), dataViewId).visualizationKey) &&
    hierarchy.usesScaleDependentHierarchyLevel &&
    hierarchy.supportsScaleDependentHierarchyControl,
  );
}

export function createHierarchyControlMenuActions(
  dataViewId: DataViewId,
  hierarchy: WorkbookAttributeDto | HierarchyInfo,
  { disabledFn }: { disabledFn?: DisabledFn } = {},
): MenuItemGroup {
  const { id, supportsScaleDependentHierarchyControl } = hierarchy;
  let calculatedDisabledFn = disabledFn;
  if (!isAnalysisContextFilterActive(dataViewId)) {
    calculatedDisabledFn = () => true;
  }
  const items = [
    createHierarchyContextLocalControlMenuAction(dataViewId, id, {
      disabledFn,
      checked: isHierarchyControlledLocally(dataViewId, hierarchy),
    }),
    createHierarchyContextGlobalControlMenuAction(dataViewId, id, {
      disabledFn: calculatedDisabledFn,
      checked: !hierarchy.usesLocalHierarchyLevel,
    }),
  ];
  if (
    isMap(getDataViewById(getStore().getState(), dataViewId).visualizationKey) &&
    supportsScaleDependentHierarchyControl
  ) {
    items.push(
      createHierarchyContextMapScaleControlMenuAction(dataViewId, id, {
        disabledFn,
        checked: isHierarchyControlledByMapScale(dataViewId, hierarchy),
      }),
    );
  }

  return {
    label: i18n('hierarchy.control.menugroup.title'),
    group: items,
    groupType: 'radio',
  };
}

function createHierarchyContextLocalControlMenuAction(
  dataViewId: DataViewId,
  id: AttributeId | ActiveAttributeId,
  { disabledFn = () => false, checked = false }: MenuActionOptions = {},
) {
  return {
    label: i18n('hierarchy.control.local'),
    icon: icon(indicatorLocalContextSvg),
    execute: () =>
      changeHierarchyControl(dataViewId, id, [
        [AttributeModificationKey.USE_SCALE_DEPENDENT_HIERARCHY_LEVEL, 'false'],
        [AttributeModificationKey.USE_LOCAL_HIERARCHY_LEVEL, 'true'],
      ]),
    disabled: disabledFn(),
    checked,
  };
}

function createHierarchyContextGlobalControlMenuAction(
  dataViewId: DataViewId,
  id: AttributeId | ActiveAttributeId,
  { disabledFn = () => false, checked = false }: MenuActionOptions = {},
) {
  return {
    label: i18n('hierarchy.control.global'),
    icon: icon(indicatorAnalysisContextSvg),
    execute: () =>
      changeHierarchyControl(dataViewId, id, [
        [AttributeModificationKey.USE_SCALE_DEPENDENT_HIERARCHY_LEVEL, 'false'],
        [AttributeModificationKey.USE_LOCAL_HIERARCHY_LEVEL, 'false'],
      ]),
    disabled: disabledFn(),
    checked,
  };
}

function createHierarchyContextMapScaleControlMenuAction(
  dataViewId: DataViewId,
  id: AttributeId | ActiveAttributeId,
  { disabledFn = () => false, checked = false }: MenuActionOptions = {},
) {
  return {
    label: i18n('hierarchy.control.map-scale-dependent'),
    icon: icon(scaleDependentIcon, { size: 'xs' }),
    execute: () =>
      changeHierarchyControl(dataViewId, id, [
        [AttributeModificationKey.USE_SCALE_DEPENDENT_HIERARCHY_LEVEL, 'true'],
        [AttributeModificationKey.USE_LOCAL_HIERARCHY_LEVEL, 'true'],
      ]),
    disabled: disabledFn(),
    checked,
  };
}

function changeHierarchyControl(
  dataViewId: DataViewId,
  id: AttributeId | ActiveAttributeId,
  modifications: [AttributeModificationKey, string | null][],
) {
  const attributeDto = getAttributeById(getStore().getState(), id);
  const attributeSettings = attributeDto.settings ?? {};
  modifications = modifications.filter(([modification, value]) => value !== attributeSettings[modification]);
  if (modifications.length) {
    assertActiveAttributeId(id, attributeDto);
    modifyDataViewAttribute(dataViewId, id, modifications);
  }
}

function isHierarchyControlledLocally(dataViewId: DataViewId, hierarchy: WorkbookAttributeDto | HierarchyInfo) {
  return hierarchy.usesLocalHierarchyLevel && !isHierarchyControlledByMapScale(dataViewId, hierarchy);
}
